import { refreshAccountData } from "api/account";
import { getConfig, getShopConfig } from "api/config";
import { refreshNotifications } from "api/notifications";
import NotConnected from "components/NotConnected/NotConnected";
import ScrollToTop from "components/ScrollToTop/ScrollToTop";
import SplashScreen from "components/SplashScreen/SplashScreen";
import Maintenance from "pages/App/Maintenance/Maintenance";
import React, { useCallback, useEffect, useState } from "react";
import { Offline, Online } from "react-detect-offline";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import RootSwitch from "RootSwitch";
import { setConfig, setShopConfig } from "store/actions/configActions";
import useGoogleAnalytics from "hooks/useGoogleAnalytics";
import { getNewsletterCampaigns } from "api/newsletter";
import FullPagePopup from "components/FullPagePopup/FullPagePopup";
import NewsletterCampaign from "templates/NewsletterCampaign/NewsletterCampaign";
import consola from "consola";
import Cookies from "universal-cookie"

const App = () => {
  useGoogleAnalytics();
  const [loading, setloading] = useState(true);
  const [maintenance, setmaintenance] = useState(false);
  const [theme, setTheme] = useState("Default");
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const auth = useSelector((state) => state.auth);
  const languages = useSelector((state) => state.config.languages);
  const [campaigns, setcampaigns] = useState([]);

  const stopLoading = () => setloading(false);

  const loadDynamicTheme = async (theme)  =>  {
    try {
      await import(`./components/Theme/${theme}.scss`);
    } catch (error) {
      console.error(`Error loading in the theme file: ${error}`);
    }
  };

  const fetchConfig = useCallback(async () => {
    const data = await getConfig();
    setmaintenance(data.maintenance);
    loadDynamicTheme(data.theme);
    setTheme(data.theme);
    dispatch(setConfig(data));
    const cookies = new Cookies();
    cookies.set('theme', data.theme, { path: '/' });
    consola.success("Config fetched successfully", data);
  }, [dispatch]);

  const fetchShopConfig = useCallback(async () => {
    const data = await getShopConfig();
    dispatch(setShopConfig(data.shop_enabled));
    consola.success("Shop config fetched successfully", data);
  }, [dispatch]);

  const refreshData = () => {
    // Prevent refresh if app is not visible
    // (will refresh everytime user tabs out otherwise)
    if (document.visibilityState !== "visible") {
      return;
    }

    refreshAccountData();
    refreshNotifications();
  };

  useEffect(() => {
    
  }, []);

  useEffect(() => {
    const promises = [fetchConfig()];

    if (auth.access_token) {
      promises.push(fetchShopConfig());
    }

    Promise.all(promises).then(stopLoading);

    let intervalId = null;

    if (auth.access_token) {
      refreshData();
      getNewsletterCampaigns().then((data) => {
        if (!Array.isArray(data)) {
          return;
        }

        setcampaigns(data);
      });
      document.addEventListener("visibilitychange", refreshData, false);
      intervalId = setInterval(refreshData, 60 * 1000);
    }

    return () => {
      document.removeEventListener("visibilitychange", refreshData, false);
      clearInterval(intervalId);
    };
  }, [auth.access_token, i18n.language, fetchConfig, fetchShopConfig]);

  useEffect(() => {
    if (!i18n.language) {
      return;
    }

    const locale = i18n.language.replace("-", "_");

    const language = languages.find((lang) => lang.locale === locale);

    if (!language) {
      return;
    }

    if (language.rtl) {
      document.body.classList.add("rtl");
    }


    return () => {
      document.body.classList.remove("rtl");
    };
  }, [i18n.language, languages]);

  if (theme) {
    document.body.classList.add(theme);
  }

  if (maintenance) {
    return <Maintenance />;
  }

  return (
    <>
      <ScrollToTop />

      <Online polling={{ url: "/internet-check.txt", interval: 30000 }}>
        {loading ? (
          <SplashScreen/>
        ) : (
          <>
            <RootSwitch />
            {campaigns.map((campaign) => (
              <FullPagePopup key={campaign.id} maxContentWidth={360}>
                <NewsletterCampaign
                  id={campaign.id}
                  title={campaign.name}
                  description={campaign.description}
                  acceptLabel={campaign.acceptLabel}
                  refusalLabel={campaign.refusalLabel}
                  link={campaign.link}
                  linkTitle={campaign.linkTitle}
                />
              </FullPagePopup>
            ))}
          </>
        )}
      </Online>
      <Offline polling={{ url: "/internet-check.txt", interval: 30000 }}>
        <NotConnected/>
      </Offline>
    </>
  );
};

export default App;
